/**
 * Автоплей видео на карточках: «видео всегда играют» — но играет только то, что
 * реально видно, и не больше лимита одновременно.
 *
 * Почему не просто autoplay на каждом <video> (замер 12.09 на трендах):
 *   • в кадре десктопа 7 роликов — при старте они выкачиваются ЦЕЛИКОМ, 24 МБ;
 *   • на телефоне в кадре 3 — 10 МБ, на 3G это минуты и рваное видео;
 *   • карточка при этом стоит ~20 КБ постером — то есть дороже в полтораста раз.
 * Поэтому: играем ближайшие к центру экрана, остальные держат постер; ушло за
 * экран — пауза и снятие src (освобождает декодер и возвращает постер, который
 * после первого play() сам уже не вернётся — так устроена спека <video>).
 *
 * Вес самих файлов лечится на бэке (`<key>_loop.mp4`, длинная сторона 640, без
 * звука) — здесь только «сколько и когда».
 *
 * Когда (30.09, первая загрузка трендов на медленном интернете): ролики не стартуют, пока страница не показана и
 * не догружена (bootShell.whenPageSettled), и запускаются ПО ОДНОМУ — следующий, когда предыдущий заиграл (или
 * через START_TIMEOUT_MS). Раньше три ролика по 0,5–0,9 МБ начинали качаться разом, вместе со стилями, скриптом
 * и данными страницы. Файлы, их качество и число одновременно играющих — прежние.
 */
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'

import { whenPageSettled } from '../bootShell'

/** Одновременно играющих: телефон бережём сильнее — там и канал, и декодер. */
const MAX_MOBILE = 3
const MAX_DESKTOP = 6
const MOBILE_WIDTH = 768
/** Фора уже играющей плитке, px. Видимых больше лимита — без форы набор
 *  «ближайших к центру» дребезжал бы от любого пересчёта: плитка замирала и
 *  стартовала заново с первого кадра на ровном месте (поймано замером). */
const HOLD_BONUS = 240
/** Сколько ждём, пока запущенный ролик заиграет, прежде чем пустить следующий (битый файл, обрыв сети). */
const START_TIMEOUT_MS = 6000

interface Item {
	el: HTMLVideoElement
	setActive: (v: boolean) => void
	visible: boolean
	active: boolean
	/** Курсор на плитке — она занимает слот вне очереди и удерживает его после
	 *  ухода мыши: «навёл — смотрю с начала, увёл — ролик продолжается». */
	hovered: boolean
	/** Ролик уже заиграл (событие playing) — следующий в очереди может стартовать. */
	playing: boolean
	/** Когда плитке дали слот (performance.now()). */
	startedAt: number
}

const items = new Set<Item>()
let observer: IntersectionObserver | null = null
let scheduled = false
let listening = false
/** Страница показана и догружена — можно запускать ролики (до этого играет только то, на что навели). */
let settled = false
let settleWatch = false
/** Перепроверка очереди, если запущенный ролик так и не заиграл. Один таймер на менеджер. */
let startTimer: ReturnType<typeof setTimeout> | null = null

/** Автоплей уместен? Движение в экране — не всем и не всегда. */
export function cardAutoplayAllowed(): boolean {
	if (typeof window === 'undefined' || typeof navigator === 'undefined') return false
	// Prerender-снапшот (scripts/prerender.mjs, puppeteer) ждёт networkidle2 —
	// играющие ролики держали бы соединения и тянули байты ради статики, которой
	// видео всё равно не достанется.
	if (navigator.webdriver) return false
	// Человек попросил меньше движения — уважаем: остаётся постер и наведение.
	if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return false
	const conn = (navigator as { connection?: { saveData?: boolean; effectiveType?: string } }).connection
	if (conn?.saveData) return false
	if (conn?.effectiveType && (conn.effectiveType === '2g' || conn.effectiveType === 'slow-2g')) return false
	return true
}

const limit = () => (window.innerWidth < MOBILE_WIDTH ? MAX_MOBILE : MAX_DESKTOP)

/** Насколько плитка далека от центра экрана — по ней и выбираем, кому играть. */
function distance(el: HTMLVideoElement): number {
	const r = el.getBoundingClientRect()
	return Math.abs((r.top + r.bottom) / 2 - window.innerHeight / 2)
}

function apply() {
	scheduled = false
	const hidden = typeof document !== 'undefined' && document.hidden
	// Расстояние меряем по разу на плитку: в компараторе sort это давало бы
	// O(n log n) чтений getBoundingClientRect на каждый кадр скролла. Играющей
	// плитке даём фору — иначе набор дребезжит (см. HOLD_BONUS).
	const near = hidden
		? []
		: [...items]
				.filter((i) => i.visible || i.hovered)
				.map((i) => ({
					i,
					// Под курсором — всегда первый; играющая плитка держит слот форой.
					d: i.hovered ? -1e6 : distance(i.el) - (i.active ? HOLD_BONUS : 0),
				}))
	const play = near.sort((a, b) => a.d - b.d).slice(0, limit()).map((x) => x.i)
	const keep = new Set(play)
	for (const item of items) {
		if (!item.active || keep.has(item)) continue
		item.active = false
		item.playing = false
		item.setActive(false)
	}
	// Новые — по одному: следующий, когда запущенный заиграл (или вышел срок ожидания). Ближайшие к центру
	// первыми; плитка под курсором — вне очереди (человек сам попросил).
	const now = performance.now()
	let starting = [...items].some((i) => i.active && !i.playing && now - i.startedAt < START_TIMEOUT_MS)
	for (const item of play) {
		if (item.active) continue
		if (!item.hovered && (!settled || starting)) continue
		item.active = true
		item.playing = false
		item.startedAt = now
		item.setActive(true)
		if (!item.hovered) starting = true
	}
	if (starting && !startTimer) {
		startTimer = setTimeout(() => {
			startTimer = null
			schedule()
		}, START_TIMEOUT_MS)
	}
}

function schedule() {
	if (scheduled) return
	scheduled = true
	requestAnimationFrame(apply)
}

function ensureObserver(): IntersectionObserver {
	if (!observer) {
		observer = new IntersectionObserver(
			(entries) => {
				for (const e of entries) {
					const item = [...items].find((i) => i.el === e.target)
					if (item) item.visible = e.isIntersecting
				}
				schedule()
			},
			// Порог 0.25: плитка, заехавшая в экран краем, ещё не «видна» — иначе
			// при скролле лимит тратится на то, чего человек толком не видит.
			{ threshold: 0.25 },
		)
	}
	if (!settleWatch) {
		settleWatch = true
		whenPageSettled(() => {
			settled = true
			schedule()
		})
	}
	if (!listening) {
		listening = true
		// Скролл меняет не видимость, а РАССТОЯНИЕ до центра — порядок кандидатов.
		window.addEventListener('scroll', schedule, { passive: true })
		window.addEventListener('resize', schedule, { passive: true })
		document.addEventListener('visibilitychange', schedule)
	}
	return observer
}

/**
 * Плитка сообщает о себе менеджеру и получает ответ: играть сейчас или нет.
 * `active=false` — компонент обязан СНЯТЬ src (вернётся постер), а не просто
 * поставить на паузу: иначе плитка застывает на последнем кадре.
 */
export function useCardVideoAutoplay(
	ref: RefObject<HTMLVideoElement | null>,
	enabled: boolean,
): { active: boolean; setHovered: (on: boolean) => void } {
	const [active, setActive] = useState(false)
	const itemRef = useRef<Item | null>(null)

	useEffect(() => {
		const el = ref.current
		if (!el || !enabled || !cardAutoplayAllowed()) return
		const item: Item = { el, setActive, visible: false, active: false, hovered: false, playing: false, startedAt: 0 }
		itemRef.current = item
		items.add(item)
		const io = ensureObserver()
		io.observe(el)
		schedule()
		return () => {
			io.unobserve(el)
			items.delete(item)
			itemRef.current = null
			schedule()
		}
	}, [ref, enabled])

	// Ховер — не отдельный режим воспроизведения, а приоритет в менеджере:
	// иначе плитка, игравшая «от мыши», после ухода курсора выпадала из набора
	// и останавливалась, хотя должна продолжать.
	const setHovered = useCallback((on: boolean) => {
		const item = itemRef.current
		if (!item || item.hovered === on) return
		item.hovered = on
		schedule()
	}, [])

	// Эффект идёт ПОСЛЕ того, как React обновил атрибут src.
	//   играем → play() (ошибку глушим: автоплей могли запретить политикой);
	//   не играем → pause() И load(). Именно load(): по спеке снятие атрибута
	//   src само по себе НЕ останавливает элемент — он доигрывает из буфера
	//   (замер: лимит «6 одновременно» держал 9), а load() ещё и возвращает
	//   постер, который после первого play() иначе не вернуть.
	useEffect(() => {
		const el = ref.current
		if (!el) return
		if (active) {
			// Заиграл — очередь пускает следующий ролик.
			const onPlaying = () => {
				const item = itemRef.current
				if (item && !item.playing) {
					item.playing = true
					schedule()
				}
			}
			el.addEventListener('playing', onPlaying)
			void el.play().catch(() => {})
			return () => el.removeEventListener('playing', onPlaying)
		}
		el.pause()
		if (!el.getAttribute('src')) el.load()
	}, [active, ref])

	return { active, setHovered }
}
